import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Send, Check } from 'lucide-react';
import { Motif } from './Ornaments';

const API = process.env.REACT_APP_BACKEND_URL;
const RESET_DELAY_MS = 300;

const OVERLAY_HIDDEN = { opacity: 0 };
const OVERLAY_SHOWN = { opacity: 1 };
const OVERLAY_TRANSITION = { duration: 0.25 };
const CARD_HIDDEN = { opacity: 0, y: 30, scale: 0.96 };
const CARD_SHOWN = { opacity: 1, y: 0, scale: 1 };
const CARD_EXIT = { opacity: 0, y: 20, scale: 0.97 };
const CARD_TRANSITION = { duration: 0.35, ease: [0.22, 1, 0.36, 1] };
const EMPTY_FORM = { name: '', email: '', message: '' };

export const ContactForm = ({ open, onClose }) => {
  const [form, setForm] = useState(EMPTY_FORM);
  const [status, setStatus] = useState('idle');
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('All fields are required.');
      return;
    }
    setStatus('sending');
    setError('');
    try {
      const res = await fetch(`${API}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail?.[0]?.msg || data.detail || 'Something went wrong.');
      }
      setStatus('sent');
      setForm(EMPTY_FORM);
    } catch (err) {
      setError(err.message || 'Could not send. Please try again.');
      setStatus('idle');
    }
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => { setStatus('idle'); setError(''); }, RESET_DELAY_MS);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="contact-overlay"
          data-testid="contact-overlay"
          initial={OVERLAY_HIDDEN}
          animate={OVERLAY_SHOWN}
          exit={OVERLAY_HIDDEN}
          transition={OVERLAY_TRANSITION}
          onClick={(e) => { if (e.target === e.currentTarget) handleClose(); }}
        >
          <motion.div
            className="contact-form-card"
            data-testid="contact-form-card"
            initial={CARD_HIDDEN}
            animate={CARD_SHOWN}
            exit={CARD_EXIT}
            transition={CARD_TRANSITION}
          >
            <div className="contact-form-header">
              <div>
                <span className="eyebrow" data-testid="contact-form-label">A NOTE, FROM YOU TO ME</span>
                <h3 data-testid="contact-form-heading">Say hello.</h3>
              </div>
              <button className="contact-form-close" data-testid="contact-form-close" onClick={handleClose} aria-label="Close contact form">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>
            <Motif className="contact-form-motif" variant="blue" />

            {status === 'sent' ? (
              <div className="contact-success" data-testid="contact-success">
                <Check size={32} strokeWidth={1.5} />
                <h4>Message sent.</h4>
                <p>Thank you for reaching out. I'll get back to you soon.</p>
                <button className="text-link" data-testid="contact-close-after-send" onClick={handleClose}>
                  CLOSE <span>→</span>
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <div className="contact-field">
                  <label htmlFor="contact-name" className="eyebrow">NAME</label>
                  <input
                    id="contact-name"
                    data-testid="contact-name-input"
                    type="text"
                    placeholder="Your name"
                    value={form.name}
                    onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                    autoComplete="name"
                  />
                </div>
                <div className="contact-field">
                  <label htmlFor="contact-email" className="eyebrow">EMAIL</label>
                  <input
                    id="contact-email"
                    data-testid="contact-email-input"
                    type="email"
                    placeholder="your@email.com"
                    value={form.email}
                    onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                    autoComplete="email"
                  />
                </div>
                <div className="contact-field">
                  <label htmlFor="contact-message" className="eyebrow">MESSAGE</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    placeholder="What's on your mind?"
                    rows={4}
                    value={form.message}
                    onChange={(e) => setForm(f => ({ ...f, message: e.target.value }))}
                  />
                </div>
                {error && <p className="contact-error" data-testid="contact-error">{error}</p>}
                <button
                  type="submit"
                  className="contact-submit"
                  data-testid="contact-submit"
                  disabled={status === 'sending'}
                >
                  {status === 'sending' ? 'SENDING...' : 'SEND MESSAGE'}
                  <Send size={14} strokeWidth={1.5} />
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
